// TodayAttendanceDashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTodaySummary,
  fetchLiveAttendance,
  updateAttendanceStatus,
  updateAttendancePunches,
} from '../features/attendance/attendanceThunks';
import {
  selectTodaySummary,
  selectTodayLoading,
  selectTodayError,
  selectLiveAttendance,
  selectLiveLoading,
  selectEditLoading,
  selectEditError,
  clearEditError,
  updateLocalStatus,
} from '../features/attendance/attendanceSlice';
import {
  isAdminOrHR,        // canUpdateAttendance: ADMIN and HR ONLY
  canUpdateAttendance,
} from '../RoleBasedRoute';
import '../CSS/TodayAttendanceDashboard.css';
import Sidebar from './Sidebar';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'PRESENT',       label: 'Present' },
  { value: 'LATE',          label: 'Late' },
  { value: 'ABSENT',        label: 'Absent' },
  { value: 'HALF_DAY',      label: 'Half Day' },
  { value: 'LEAVE',         label: 'Leave' },
  { value: 'MEDICAL_LEAVE', label: 'Medical Leave' },
  { value: 'WEEK_OFF',      label: 'Week Off' },
];

// Extract "HH:MM" from an ISO string, displayed in IST
function isoToTimeInput(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Extract "YYYY-MM-DD" from an ISO string, in IST
function isoToDateInput(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Combine a date string + time string into a proper IST ISO string for the backend
function combineToIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return new Date(`${dateStr}T${timeStr}:00+05:30`).toISOString();
}

// ─── Skeleton Components ──────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="today-skeleton-card">
    <div className="today-skeleton-number"></div>
    <div className="today-skeleton-text"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="today-skeleton-row">
    {[...Array(7)].map((_, i) => (
      <td key={i}><div className="today-skeleton-cell"></div></td>
    ))}
  </tr>
);

// ─── Status CSS helper ────────────────────────────────────────────────────────
const getStatusClass = (status) => {
  const map = {
    'PRESENT':       'today-status-present',
    'LATE':          'today-status-late',
    'ABSENT':        'today-status-absent',
    'HALF_DAY':      'today-status-halfday',
    'LEAVE':         'today-status-leave',
    'MEDICAL_LEAVE': 'today-status-medical',
    'WEEK_OFF':      'today-status-weekoff',
  };
  return map[status] || 'today-status-default';
};

// ─── Night-shift carry-over badge ─────────────────────────────────────────────
// Shown on rows where the record is dated yesterday but the employee is still
// in the office (punch_in set, punch_out null, date = yesterday).
const NightShiftBadge = () => (
  <span
    className="today-night-shift-badge"
    title="Night shift carry-over — this record is from yesterday; the employee is still in the office"
  >
    🌙 Night
  </span>
);

// ─── Edit Modal ───────────────────────────────────────────────────────────────
// Only rendered when the current user has edit rights (ADMIN or HR).
const EditAttendanceModal = ({ record, onClose, onSave, editLoading, editError }) => {
  const [activeTab,      setActiveTab]      = useState('status');
  const [selectedStatus, setSelectedStatus] = useState(record.status || 'PRESENT');
  const [statusRemarks,  setStatusRemarks]  = useState('');
  // Separate date + time states for each punch.
  // Date defaults to record.date but the admin can change it — cleanly handles
  // night-shift punch-outs that fall on the next calendar day.
  const recordDate = record.attendance_date || record.date;
  const [punchInDate,    setPunchInDate]    = useState(isoToDateInput(record.punch_in)  || recordDate);
  const [punchInTime,    setPunchInTime]    = useState(isoToTimeInput(record.punch_in));
  const [punchOutDate,   setPunchOutDate]   = useState(isoToDateInput(record.punch_out) || recordDate);
  const [punchOutTime,   setPunchOutTime]   = useState(isoToTimeInput(record.punch_out));
  const [punchRemarks,   setPunchRemarks]   = useState('');

  // Auto-advance punch-out date by 1 when the typed time is earlier than punch-in
  // (only when both are on the same date — crossing midnight is the logical reason)
  const handlePunchOutTimeChange = (val) => {
    setPunchOutTime(val);
    if (punchInTime && val && punchOutDate === punchInDate && val < punchInTime) {
      const nextDay = new Date(new Date(`${punchInDate}T00:00:00+05:30`).getTime() + 86400000)
        .toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      setPunchOutDate(nextDay);
    }
  };

  const handleSave = () => {
    if (activeTab === 'status') {
      onSave({ type: 'status', recordId: record.id, status: selectedStatus, remarks: statusRemarks, oldStatus: record.status });
    } else {
      const punch_in  = punchInTime  ? combineToIso(punchInDate,  punchInTime)  : null;
      const punch_out = punchOutTime ? combineToIso(punchOutDate, punchOutTime) : null;
      if (!punch_in && !punch_out) return;
      onSave({ type: 'punches', recordId: record.id, punch_in, punch_out, remarks: punchRemarks });
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="today-modal-backdrop" onClick={handleBackdrop}>
      <div className="today-modal">

        {/* Header */}
        <div className="today-modal-header">
          <div>
            <h2 className="today-modal-title">Edit Attendance</h2>
            <p className="today-modal-subtitle">
              <span className="today-modal-name">{record.employee_name}</span>
              <span className="today-modal-code"> · #{record.employee_code}</span>
              {record.is_night_shift_carry && (
                <span className="today-modal-night-tag"> · 🌙 Night shift (from yesterday)</span>
              )}
            </p>
          </div>
          <button className="today-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Tab Toggle */}
        <div className="today-modal-tabs">
          <button
            className={`today-modal-tab ${activeTab === 'status' ? 'today-modal-tab-active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            ⬤ Status
          </button>
          <button
            className={`today-modal-tab ${activeTab === 'punches' ? 'today-modal-tab-active' : ''}`}
            onClick={() => setActiveTab('punches')}
          >
            🕐 Punch Times
          </button>
        </div>

        {/* Body */}
        <div className="today-modal-body">
          {activeTab === 'status' ? (
            <>
              <div className="today-modal-current">
                <span className="today-modal-label">Current Status</span>
                <span className={`today-status-badge today-modal-current-badge ${getStatusClass(record.status)}`}>
                  {record.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="today-modal-field">
                <label className="today-modal-label">New Status</label>
                <div className="today-modal-status-grid">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`today-modal-status-btn ${selectedStatus === opt.value ? 'today-modal-status-selected' : ''} today-modal-status-${opt.value.toLowerCase()}`}
                      onClick={() => setSelectedStatus(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="today-modal-field">
                <label className="today-modal-label">
                  Remarks <span className="today-modal-optional">(optional)</span>
                </label>
                <textarea
                  className="today-modal-textarea"
                  placeholder="Reason for status change..."
                  value={statusRemarks}
                  onChange={e => setStatusRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          ) : (
            <>
              <div className="today-modal-punch-current">
                <div className="today-modal-punch-info">
                  <span className="today-modal-label">Current Punch In</span>
                  <span className="today-modal-punch-value">{isoToTimeInput(record.punch_in) || '—'}</span>
                </div>
                <div className="today-modal-punch-arrow">→</div>
                <div className="today-modal-punch-info">
                  <span className="today-modal-label">Current Punch Out</span>
                  <span className="today-modal-punch-value">{isoToTimeInput(record.punch_out) || '—'}</span>
                </div>
              </div>
              {record.is_night_shift_carry && (
                <div className="today-modal-night-note">
                  🌙 This is a night-shift carry-over record. The attendance date is{' '}
                  <strong>{record.date}</strong> (yesterday). Punch times are adjusted
                  against that date.
                </div>
              )}
              <p className="today-modal-punch-note">
                ℹ️ Status will be automatically recalculated from the new times.
              </p>
              {/* Punch-in: date + time */}
              <div className="today-modal-field">
                <label className="today-modal-label">New Punch In</label>
                <div className="today-modal-datetime-row">
                  <input
                    type="date"
                    className="today-modal-date-input"
                    value={punchInDate}
                    onChange={e => setPunchInDate(e.target.value)}
                  />
                  <input
                    type="time"
                    className="today-modal-time-input"
                    value={punchInTime}
                    onChange={e => setPunchInTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Punch-out: date + time — date may differ for night shift */}
              <div className="today-modal-field">
                <label className="today-modal-label">New Punch Out</label>
                <div className="today-modal-datetime-row">
                  <input
                    type="date"
                    className="today-modal-date-input"
                    value={punchOutDate}
                    onChange={e => setPunchOutDate(e.target.value)}
                  />
                  <input
                    type="time"
                    className="today-modal-time-input"
                    value={punchOutTime}
                    onChange={e => handlePunchOutTimeChange(e.target.value)}
                  />
                </div>
                {punchOutDate !== punchInDate && punchOutTime && (
                  <div className="today-modal-nextday-badge">
                    🌙 Punch-out is on a different day than punch-in
                    {punchOutDate > punchInDate ? ' (+next day)' : ' (⚠ earlier date)'}
                  </div>
                )}
              </div>
              <div className="today-modal-field">
                <label className="today-modal-label">
                  Remarks <span className="today-modal-optional">(optional)</span>
                </label>
                <textarea
                  className="today-modal-textarea"
                  placeholder="Reason for correction (e.g. biometric reader error)..."
                  value={punchRemarks}
                  onChange={e => setPunchRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}
          {editError && <div className="today-modal-error">⚠ {editError}</div>}
        </div>

        {/* Footer */}
        <div className="today-modal-footer">
          <button className="today-modal-btn-cancel" onClick={onClose} disabled={editLoading}>
            Cancel
          </button>
          <button
            className="today-modal-btn-save"
            onClick={handleSave}
            disabled={editLoading || (activeTab === 'punches' && !punchInTime && !punchOutTime)}
          >
            {editLoading ? (
              <span className="today-modal-saving">
                <span className="today-modal-spinner"></span> Saving...
              </span>
            ) : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TodayAttendanceDashboard = () => {
  const dispatch       = useDispatch();
  const user           = useSelector((state) => state.auth.user);
  const todaySummary   = useSelector(selectTodaySummary);
  const loading        = useSelector(selectTodayLoading);
  const error          = useSelector(selectTodayError);
  const liveAttendance = useSelector(selectLiveAttendance);
  const liveLoading    = useSelector(selectLiveLoading);
  const editLoading    = useSelector(selectEditLoading);
  const editError      = useSelector(selectEditError);

  const [searchTerm,   setSearchTerm]   = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeTab,    setActiveTab]    = useState('all'); // 'all' | 'live'
  const [editRecord,   setEditRecord]   = useState(null);
  const [successMsg,   setSuccessMsg]   = useState('');

  // PERMISSION: only ADMIN and HR can update attendance status / punch times.
  // Team Managers (rank 5–8) can VIEW records but the Edit button is hidden.
  const canEdit = canUpdateAttendance(user);

  // Fetch on mount
  useEffect(() => {
    dispatch(fetchTodaySummary());
    dispatch(fetchLiveAttendance());
  }, [dispatch]);

  // Auto-refresh every 60 s
  useEffect(() => {
    const id = setInterval(() => {
      dispatch(fetchTodaySummary());
      dispatch(fetchLiveAttendance());
    }, 60000);
    return () => clearInterval(id);
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchTodaySummary());
    dispatch(fetchLiveAttendance());
  };

  const openEdit = useCallback((record) => {
    dispatch(clearEditError());
    setEditRecord(record);
    setSuccessMsg('');
  }, [dispatch]);

  const closeEdit = () => {
    setEditRecord(null);
    dispatch(clearEditError());
  };

  const handleSave = useCallback(async (payload) => {
    let result;
    if (payload.type === 'status') {
      dispatch(updateLocalStatus({ recordId: payload.recordId, newStatus: payload.status }));
      result = await dispatch(updateAttendanceStatus({
        recordId:  payload.recordId,
        status:    payload.status,
        remarks:   payload.remarks,
        oldStatus: payload.oldStatus,
      }));
    } else {
      result = await dispatch(updateAttendancePunches({
        recordId:  payload.recordId,
        punch_in:  payload.punch_in,
        punch_out: payload.punch_out,
        remarks:   payload.remarks,
      }));
    }
    if (!result.error) {
      setSuccessMsg(
        payload.type === 'status'
          ? `Status updated to ${payload.status.replace(/_/g, ' ')}`
          : 'Punch times updated successfully'
      );
      setEditRecord(null);
      dispatch(fetchTodaySummary());
    }
  }, [dispatch]);

  const filteredRecords = todaySummary.records.filter(record => {
    const matchesSearch =
      record.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.employee_code.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'ALL' || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Always display in IST (backend stores IST)
  const formatTime = (datetime) => {
    if (!datetime) return '--:--';
    return new Date(datetime).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  // ── Skeleton loading ──────────────────────────────────────────────────────
  if (loading && !todaySummary.records.length) {
    return (
      <div className="index-box">
        <Sidebar />
        <div className="today-attendance-dashboard">
          <div className="today-dashboard-header">
            <div className="today-header-left">
              <div className="today-skeleton-title"></div>
              <div className="today-skeleton-subtitle"></div>
            </div>
            <div className="today-header-right">
              <div className="today-skeleton-button"></div>
            </div>
          </div>
          <div className="today-summary-cards">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
          <div className="today-tabs-container">
            <div className="today-skeleton-tab"></div>
            <div className="today-skeleton-tab"></div>
          </div>
          <div className="today-attendance-table-container">
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th><th>Employee Name</th>
                  <th>Check In</th><th>Check Out</th>
                  <th>Work Hours</th><th>Status</th>
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => <SkeletonTableRow key={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="index-box">
        <Sidebar />
        <div className="today-attendance-dashboard">
          <div className="today-error-container">
            <p className="today-error-message">{error}</p>
            <button onClick={handleRefresh} className="today-btn-retry">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Render ───────────────────────────────────────────────────────────
  return (
    <div className="index-box">
      <Sidebar />
      <div className="today-attendance-dashboard">

        {/* Header */}
        <div className="today-dashboard-header">
          <div className="today-header-left">
            <h1>Today's Attendance</h1>
            <p className="today-date-display">
              {todaySummary.date
                ? new Date(todaySummary.date + 'T00:00:00+05:30').toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata',
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                  })
                : new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
            </p>
          </div>
          <div className="today-header-right">
            {successMsg && <span className="today-success-toast">{successMsg}</span>}
            <button onClick={handleRefresh} className="today-btn-refresh" disabled={loading}>
              <span className={loading ? 'today-refreshing' : ''}>↻</span> Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="today-summary-cards">
          <div className="today-summary-card today-card-total">
            <div className="today-card-content">
              <h3>{todaySummary.totalEmployees}</h3><p>Total Employees</p>
            </div>
          </div>
          <div className="today-summary-card today-card-present">
            <div className="today-card-content">
              <h3>{todaySummary.present}</h3><p>Present</p>
            </div>
          </div>
          <div className="today-summary-card today-card-late">
            <div className="today-card-content">
              <h3>{todaySummary.late}</h3><p>Late</p>
            </div>
          </div>
          <div className="today-summary-card today-card-absent">
            <div className="today-card-content">
              <h3>{todaySummary.employeesWithoutRecords}</h3><p>Not Checked In</p>
            </div>
          </div>
          <div className="today-summary-card today-card-leave">
            <div className="today-card-content">
              <h3>{todaySummary.onLeave}</h3><p>On Leave</p>
            </div>
          </div>
          <div className="today-summary-card today-card-live">
            <div className="today-card-content">
              <h3>{liveAttendance.length}</h3><p>Currently In Office</p>
            </div>
          </div>
        </div>

        {/* View-only banner for non-edit roles */}
        {!canEdit && (
          <div className="today-viewonly-banner">
       
          </div>
        )}

        {/* Tabs */}
        <div className="today-tabs-container">
          <button
            className={`today-tab-btn ${activeTab === 'all' ? 'today-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Attendance ({todaySummary.total})
          </button>
          <button
            className={`today-tab-btn ${activeTab === 'live' ? 'today-active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            Live — In Office ({liveAttendance.length})
          </button>
        </div>

        {/* Filters — All tab only */}
        {activeTab === 'all' && (
          <div className="today-filters-section">
            <div className="today-search-box">
              <input
                type="text"
                placeholder="Search by name or employee code..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="today-search-input"
              />
            </div>
            <div className="today-filter-buttons">
              {[
                { key: 'ALL',     label: `All (${todaySummary.total})` },
                { key: 'PRESENT', label: `Present (${todaySummary.present})` },
                { key: 'LATE',    label: `Late (${todaySummary.late})` },
                { key: 'ABSENT',  label: `Absent (${todaySummary.absent})` },
              ].map(f => (
                <button
                  key={f.key}
                  className={`today-filter-btn ${statusFilter === f.key ? 'today-active' : ''}`}
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Tables ────────────────────────────────────────────────────────── */}
        {activeTab === 'all' ? (
          <div className="today-attendance-table-container">
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Hours</th>
                  <th>Status</th>
                  {/* Actions column only rendered for ADMIN/HR */}
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map(record => (
                    <tr
                      key={record.id}
                      className={record.is_night_shift_carry ? 'today-night-shift-row' : ''}
                    >
                      <td>{record.employee_code}</td>
                      <td className="today-employee-name">
                        {record.employee_name}
                        {record.is_night_shift_carry && <NightShiftBadge />}
                      </td>
                      <td>{formatTime(record.punch_in)}</td>
                      <td>{formatTime(record.punch_out)}</td>
                      <td>{record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}</td>
                      <td>
                        <span className={`today-status-badge ${getStatusClass(record.status)}`}>
                          {record.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      {canEdit && (
                        <td>
                          <button
                            className="today-btn-edit"
                            onClick={() => openEdit(record)}
                            title="Edit attendance"
                          >
                            ✏ Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canEdit ? 7 : 6} className="today-no-data">
                      {searchTerm || statusFilter !== 'ALL'
                        ? 'No records found matching your filters'
                        : 'No attendance records for today'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* ── Live tab ─────────────────────────────────────────────────────── */
          <div className="today-attendance-table-container">
            <div className="today-live-header">
              <h3>Employees Currently In Office</h3>
              <span className="today-live-indicator">
                <span className="today-pulse"></span> Live
              </span>
            </div>
            <table className="today-attendance-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Check In Time</th>
                  <th>Hours So Far</th>
                  <th>Status</th>
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {liveLoading ? (
                  [...Array(3)].map((_, i) => <SkeletonTableRow key={i} />)
                ) : liveAttendance.length > 0 ? (
                  liveAttendance.map(record => (
                    <tr
                      key={record.id}
                      className={`today-live-row${record.is_night_shift_carry ? ' today-night-shift-row' : ''}`}
                    >
                      <td>{record.employee_code}</td>
                      <td className="today-employee-name">
                        {record.employee_name}
                        {record.is_night_shift_carry && <NightShiftBadge />}
                      </td>
                      <td>{formatTime(record.punch_in)}</td>
                      <td>{record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}</td>
                      <td>
                        <span className={`today-status-badge ${getStatusClass(record.status)}`}>
                          {record.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      {canEdit && (
                        <td>
                          <button
                            className="today-btn-edit"
                            onClick={() => openEdit(record)}
                            title="Edit attendance"
                          >
                            ✏ Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canEdit ? 6 : 5} className="today-no-data">
                      No employees currently in office
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Stats */}
        <div className="today-footer-stats">
          <div className="today-stat-item">
            <span className="today-stat-label">Total Work Hours:</span>
            <span className="today-stat-value">{todaySummary.totalWorkHours.toFixed(2)} hrs</span>
          </div>
          <div className="today-stat-item">
            <span className="today-stat-label">Average Work Hours:</span>
            <span className="today-stat-value">{todaySummary.avgWorkHours.toFixed(2)} hrs</span>
          </div>
          <div className="today-stat-item">
            <span className="today-stat-label">Attendance Rate:</span>
            <span className="today-stat-value">
              {todaySummary.totalEmployees > 0
                ? ((todaySummary.employeesWithRecords / todaySummary.totalEmployees) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Edit Modal — only mounted when user has edit rights */}
      {editRecord && canEdit && (
        <EditAttendanceModal
          record={editRecord}
          onClose={closeEdit}
          onSave={handleSave}
          editLoading={editLoading}
          editError={editError}
        />
      )}
    </div>
  );
};

export default TodayAttendanceDashboard;